import React, { ReactFragment, useState, MouseEvent, useEffect } from 'react';
import { Grid, Button, Divider, makeStyles } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import { Loader } from 'components/Loader';
import { CreateTeamModal } from 'components/Modals/CreateTeamModal';
import { JoinCreateTeamModal } from 'components/Modals/JoinCreateTeamModal';
import { JoinTeamModal } from 'components/Modals/JoinTeamModal';
import { Team } from 'redux/models/team';
import './TeamsPanel.scss';
import { useHistory } from 'react-router-dom';
import { withRouteInformation } from 'components/withRouteInformation';
import { SendShoutOutModal } from 'components/Modals/SendShoutOutModal';

const useStyles = makeStyles({
	root: {
		flexWrap: 'nowrap',
	},
});

interface TeamsPanelProps {
	loading: boolean;
	teams: Team[];
	selectedTeamId: string | undefined;
	pathname: string;
	children: React.ReactNode;
	onTeamSelected: (teamId: string) => void;
	onToggleFavoriteTeam: (teamId: string) => void;
}

const TeamsPanel: React.FC<TeamsPanelProps> = (props: TeamsPanelProps) => {
	const classes = useStyles();
	const history = useHistory();
	const { teams, loading, selectedTeamId, pathname, onToggleFavoriteTeam } = props;
	const [pageSelected, setPageSelected] = useState('');
	const [route, setRoute] = useState('');
	let favoriteTeams: Team[] = [];
	let regularTeams: Team[] = [];
	let favoriteTeamsItems: ReactFragment[] = [];
	let regularTeamsItems: ReactFragment[] = [];
	let favoriteTeamsView;
	let regularTeamsView;

	useEffect(() => {
		if (!pathname) {
			return;
		}
		let routeArray = pathname.split('/');
		setRoute(routeArray.slice(3).join('/'));
	}, [pathname]);

	const setupTeams = (teams: Team[]) => {
		teams.forEach((team: Team) => {
			if (team.favorite) {
				favoriteTeams.push(team);
			} else {
				regularTeams.push(team);
			}
		});

		loadTeamViews(favoriteTeams, regularTeams);
	};

	const handleToggleFavoriteTeam = (e: MouseEvent<HTMLDivElement>, teamId: string) => {
		e.stopPropagation();
		onToggleFavoriteTeam(teamId);
	};

	const handleTeamClicked = (teamId: string) => {
		history.push(`/team/${teamId}/${route}`);
	};

	const loadTeamViews = (favoriteTeams: Team[], regularTeams: Team[]) => {
		favoriteTeams.forEach((team: Team, index: number) => {
			favoriteTeamsItems.push(
				<React.Fragment key={index}>
					<Grid
						onClick={() => handleTeamClicked(team.teamId)}
						item
						container
						alignItems='center'
						className={`team-grid-item ${team.teamId === selectedTeamId ? 'selected' : ''} `}
					>
						<Grid item className='team-grid-item__star-wrapper' onClick={e => handleToggleFavoriteTeam(e, team.teamId)}>
							<StarIcon className='star-icon' />
						</Grid>
						<Grid item className='team-name'>
							{team.teamName}
						</Grid>
					</Grid>
				</React.Fragment>
			);
		});

		regularTeams.forEach((team: Team, index: number) => {
			regularTeamsItems.push(
				<React.Fragment key={index}>
					<Grid
						onClick={() => handleTeamClicked(team.teamId)}
						item
						container
						alignItems='center'
						className={`team-grid-item ${team.teamId === selectedTeamId ? 'selected' : ''} `}
					>
						<Grid item className='team-grid-item__star-wrapper' onClick={e => handleToggleFavoriteTeam(e, team.teamId)}>
							<StarIcon />
						</Grid>
						<Grid item className='team-name'>
							{team.teamName}
						</Grid>{' '}
						 
					</Grid>
				</React.Fragment>
			);
		});
	};

	setupTeams(teams);

	if (loading) {
		favoriteTeamsView = <Loader />;
	} else if (favoriteTeams.length > 0 && !loading) {
		favoriteTeamsView = favoriteTeamsItems;
	} else {
		favoriteTeamsView = <div className='no-teams-message'>No favorite teams at this time!</div>;
	}

	if (loading) {
		regularTeamsView = <Loader />;
	} else if (regularTeams.length > 0 && !loading) {
		regularTeamsView = regularTeamsItems;
	} else {
		regularTeamsView = <div className='no-teams-message'>No teams at this time!</div>;
	}

	return (
		<>
			<Grid classes={{ root: classes.root }} container direction='column' alignContent='center' alignItems='center' className='team-panel-container'>
				<Grid item className='join-create-team-button-grid-item'>
					<Button variant='contained' className='panel-button shout-out-button glow--simple' onClick={() => setPageSelected('shoutout')}>
						Send Shout-Out
					</Button>
				</Grid>
				<Grid item className='join-create-team-button-grid-item'>
					<Button variant='contained' color='primary' className='panel-button join-create-team-button' onClick={() => setPageSelected('home')}>
						Join or create a new team
					</Button>
				</Grid>

				<Divider className='user-drawer-divider' />
				<div className='user-drawer-teams-wrapper'>
					<div className='user-drawer-header'>Favorites</div>
					<div className='team-list'>{favoriteTeamsView}</div>
				</div>

				<Divider className='user-drawer-divider' />
				<div className='user-drawer-teams-wrapper'>
					<div className='user-drawer-header'>My Teams</div>
					<div className='team-list'>{regularTeamsView}</div>
				</div>
			</Grid>
			<CreateTeamModal
				hideBackdrop={false}
				allowClickOutsideToClose={true}
				open={pageSelected === 'create'}
				onCloseClicked={() => setPageSelected('')}
				onBackClicked={() => setPageSelected('home')}
				onCreateTeamClicked={() => setPageSelected('')}
			/>
			<JoinTeamModal
				hideBackdrop={false}
				allowClickOutsideToClose={true}
				open={pageSelected === 'join'}
				onCloseClicked={() => setPageSelected('')}
				onBackClicked={() => setPageSelected('home')}
				onJoinTeamClicked={() => setPageSelected('')}
			/>
			<JoinCreateTeamModal
				hideBackdrop={false}
				allowClickOutsideToClose={false}
				open={pageSelected === 'home'}
				onCloseClicked={() => setPageSelected('')}
				onCreateTeamClicked={() => setPageSelected('create')}
				onJoinTeamClicked={() => setPageSelected('join')}
				onSendShoutOutClicked={() => setPageSelected('shoutout')}
			/>
			<SendShoutOutModal
				hideBackdrop={false}
				allowClickOutsideToClose={true}
				open={pageSelected === 'shoutout'}
				onCloseClicked={() => setPageSelected('')}
				onBackClicked={() => setPageSelected('home')}
				onsendShoutOutClicked={() => setPageSelected('')}
			/>
		</>
	);
};

export default withRouteInformation(TeamsPanel);
